import React from 'react';
import { CreditCard } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { createStripeCheckout } from '../utils/stripe';
import DiscountBanner from './DiscountBanner';

const Books = () => {
  return (
    <section id="books" className="py-24 px-6 bg-gradient-to-br from-pink-50 via-rose-50 to-orange-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-4">Published Works</h2>
          <div className="w-20 h-1 bg-gradient-to-r from-teal-500 via-purple-500 to-pink-500 mx-auto"></div>
        </div>

        <DiscountBanner />
        
        {/* Published Book */}
        <div className="mb-20">
          <h3 className="text-3xl font-serif font-bold text-center text-gray-800 mb-12">Featured Book</h3>
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_leadership-impact-3/artifacts/2jh8qyrz_IMG_3145.png"
                alt="Strong Roots, Strong Wings Book Cover"
                className="w-full max-w-md shadow-2xl rounded-lg hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="space-y-6">
              <h4 className="text-3xl md:text-4xl font-serif font-bold text-gray-900">
                Strong Roots, Strong Wings: How Fathers Shape Their Daughters
              </h4>
              <p className="text-lg text-gray-600 leading-relaxed">
                An insightful exploration of the profound impact fathers have on their daughters' lives, examining how these foundational relationships shape confidence, character, and future success.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Through compelling research and heartfelt narratives, this book offers valuable perspectives for fathers, daughters, and anyone interested in understanding the dynamics of parent-child relationships.
              </p>
              
              {/* Payment Options */}
              <div className="space-y-6 pt-4">
                <h5 className="text-xl font-bold text-gray-800">Choose Your Format:</h5>
                
                {/* Physical Book - Multiple Regions */}
                <div className="border-2 border-purple-200 rounded-lg p-6 bg-purple-50/50">
                  <div className="mb-4">
                    <h6 className="text-lg font-bold text-gray-900">Physical Book (Paperback)</h6>
                    <p className="text-sm text-gray-600">Select your currency</p>
                  </div>
                  <div className="space-y-3">
                    <a 
                      href="https://amzn.to/3OhCEYQ"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <Button className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-semibold">
                        Buy on Amazon (International)
                      </Button>
                    </a>
                    
                    {/* Dollar Payment */}
                    <div className="bg-white p-3 rounded border border-purple-100">
                      <p className="text-xs font-semibold text-gray-700 mb-2">💵 Pay in Dollar - $12</p>
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => createStripeCheckout('Strong Roots, Strong Wings - Physical Book', 'physical', 12, 'usd')}
                          className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white text-sm"
                        >
                          <CreditCard className="mr-2 h-3 w-3" />
                          Card/Apple/Google Pay
                        </Button>
                        <a 
                          href={`https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=abiolateejae@gmail.com&item_name=Strong Roots Strong Wings - Physical Book&amount=12.00&currency_code=USD`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1"
                        >
                          <Button className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm">
                            PayPal
                          </Button>
                        </a>
                      </div>
                    </div>

                    {/* Naira Payment */}
                    <div className="bg-white p-3 rounded border border-green-100">
                      <p className="text-xs font-semibold text-gray-700 mb-2">🇳🇬 Pay in Naira - ₦10,000</p>
                      <div className="space-y-2">
                        <Button 
                          onClick={() => createStripeCheckout('Strong Roots, Strong Wings - Physical Book', 'physical', 10000, 'ngn')}
                          className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white text-sm"
                        >
                          <CreditCard className="mr-2 h-3 w-3" />
                          Pay with Card (Naira)
                        </Button>
                        <div className="text-xs text-gray-600 bg-green-50 p-2 rounded border border-green-200">
                          <strong>Or Bank Transfer:</strong> Access Bank | 0779703304 | Abiola Oluwatobi Ibukunoluwa<br/>
                          <span className="text-green-700">Send receipt to: abiolateejae@gmail.com</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Ebook */}
                <div className="border-2 border-teal-200 rounded-lg p-6 bg-teal-50/50">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h6 className="text-lg font-bold text-gray-900">Digital Ebook</h6>
                      <p className="text-sm text-gray-600">PDF format - Instant delivery, no shipping</p>
                    </div>
                    <div className="text-2xl font-bold text-teal-600">$6</div>
                  </div>
                  <div className="space-y-3">
                    <Button 
                      onClick={() => createStripeCheckout('Strong Roots, Strong Wings - Ebook', 'ebook', 6, 'usd')}
                      className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-semibold"
                    >
                      <CreditCard className="mr-2 h-4 w-4" />
                      Pay with Card/Apple Pay/Google Pay
                    </Button>
                    <a 
                      href={`https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=abiolateejae@gmail.com&item_name=Strong Roots Strong Wings - Ebook PDF&amount=6.00&currency_code=USD`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <Button className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold">
                        Buy Ebook via PayPal ($6)
                      </Button>
                    </a>
                    <div className="text-xs text-gray-600 bg-green-50 p-3 rounded border border-green-200">
                      <strong>🇳🇬 Nigeria: ₦5,000</strong><br/>
                      Access Bank | 0779703304 | Abiola Oluwatobi Ibukunoluwa<br/>
                      <span className="text-green-700">Send receipt to: abiolateejae@gmail.com</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Kids Book Series */}
        <div className="border-t-2 border-gray-200 pt-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-serif font-bold text-gray-800 mb-4">Children's Series</h3>
            <p className="text-xl text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-purple-600 font-semibold mb-2">
              Shaping Identity Early
            </p>
            <p className="text-lg text-gray-600">Available now!</p>
          </div>

          {/* Bundle Offer */}
          <div id="daily-affirmations-bundle" className="max-w-4xl mx-auto mb-12 scroll-mt-24">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-8 text-white shadow-2xl">
              <div className="text-center mb-6">
                <h4 className="text-3xl font-bold mb-2">🎁 Special Bundle Offer!</h4>
                <p className="text-xl">Get All 3 Daily Affirmations Books Together</p>
                <a
                  href="https://wa.me/15312492610?text=Hi%21%20I%20have%20a%20question%20about%20the%20Daily%20Affirmations%20books."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-5 py-2.5 rounded-full text-sm shadow-lg hover:shadow-emerald-500/50 transition-all"
                  data-testid="daily-affirmations-whatsapp-btn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  Chat on WhatsApp
                </a>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <h5 className="text-xl font-bold mb-4">Bundle Includes:</h5>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="mr-2">✓</span>
                      <span>My Daily Affirmations Book</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">✓</span>
                      <span>My Daily Affirmations Workbook</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">✓</span>
                      <span>My Daily Affirmations Coloring Booklet</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <h5 className="text-xl font-bold mb-4">Bundle Pricing:</h5>
                  <div className="space-y-4">
                    <div>
                      <p className="text-2xl font-bold mb-2">💵 USD: $33.99</p>
                      <p className="text-sm opacity-90">(Save over 12% off individual prices)</p>
                      <div className="mt-3 space-y-2">
                        <Button
                          onClick={() => createStripeCheckout('Daily Affirmations Bundle (All 3 Books)', 'physical', 33.99, 'usd')}
                          className="w-full bg-white text-purple-600 hover:bg-gray-100 font-semibold"
                          data-testid="bundle-stripe-usd"
                        >
                          <CreditCard className="mr-2 h-4 w-4" />
                          Pay with Card/Apple/Google Pay
                        </Button>
                        <a
                          href={`https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=abiolateejae@gmail.com&item_name=Daily Affirmations Bundle - All 3 Books&amount=33.99&currency_code=USD`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="bundle-paypal-usd"
                        >
                          <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                            Order via PayPal
                          </Button>
                        </a>
                      </div>
                    </div>
                    <div className="border-t border-white/20 pt-4">
                      <p className="text-2xl font-bold mb-2">💶 EUR: €33.99</p>
                      <Button
                        onClick={() => createStripeCheckout('Daily Affirmations Bundle (All 3 Books)', 'physical', 33.99, 'eur')}
                        className="w-full bg-white text-purple-600 hover:bg-gray-100 font-semibold"
                        data-testid="bundle-stripe-eur"
                      >
                        <CreditCard className="mr-2 h-4 w-4" />
                        Pay with Card (Euro)
                      </Button>
                    </div>
                    <div className="border-t border-white/20 pt-4">
                      <p className="text-2xl font-bold mb-2">💷 GBP: £33.99</p>
                      <Button
                        onClick={() => createStripeCheckout('Daily Affirmations Bundle (All 3 Books)', 'physical', 33.99, 'gbp')}
                        className="w-full bg-white text-purple-600 hover:bg-gray-100 font-semibold"
                        data-testid="bundle-stripe-gbp"
                      >
                        <CreditCard className="mr-2 h-4 w-4" />
                        Pay with Card (Pounds)
                      </Button>
                    </div>
                    <div className="border-t border-white/20 pt-4">
                      <p className="text-2xl font-bold mb-2">🇳🇬 Naira: ₦29,000</p>
                      <p className="text-sm opacity-90">(Save ₦2,500 vs individual prices)</p>
                      <div className="mt-3 space-y-2">
                        <Button
                          onClick={() => createStripeCheckout('Daily Affirmations Bundle (All 3 Books)', 'physical', 29000, 'ngn')}
                          className="w-full bg-white text-purple-600 hover:bg-gray-100 font-semibold"
                          data-testid="bundle-stripe-ngn"
                        >
                          <CreditCard className="mr-2 h-4 w-4" />
                          Pay with Card (Naira)
                        </Button>
                        <div className="text-xs bg-green-500/20 p-2 rounded">
                          <strong>Bank Transfer:</strong> Access Bank 0779703304<br/>
                          Send receipt to: abiolateejae@gmail.com
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mb-8">
            <p className="text-gray-600 text-lg">Or purchase individually:</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            <Card id="daily-affirmations-book" className="p-6 hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-cyan-50 to-blue-50 border-2 border-cyan-200 hover:-translate-y-2 scroll-mt-24">
              <div className="mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_leadership-impact-3/artifacts/qzmgcly8_f00a1492-9909-46cb-a884-10d68162954c.jpeg"
                  alt="My Daily Affirmations Book"
                  className="w-full rounded-lg shadow-lg"
                />
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-3">My Daily Affirmations Book</h4>
              <p className="text-gray-600 mb-4">Vol. 1 - A beautiful collection of daily affirmations to build children's confidence and positive self-identity.</p>
              <div className="space-y-3">
                <div className="text-lg font-bold text-gray-900">$12.99 / €12.99 / £12.99 - Physical Book</div>
                <Button
                  onClick={() => createStripeCheckout('My Daily Affirmations Book - Physical', 'physical', 12.99, 'usd')}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white text-sm"
                  data-testid="affirm-book-stripe-usd"
                >
                  <CreditCard className="mr-2 h-3 w-3" />
                  Pay $12.99 — Card · Apple · Google
                </Button>
                <Button
                  onClick={() => createStripeCheckout('My Daily Affirmations Book - Physical', 'physical', 12.99, 'eur')}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white text-sm"
                  data-testid="affirm-book-stripe-eur"
                >
                  <CreditCard className="mr-2 h-3 w-3" />
                  Pay €12.99 — Card · Apple · Google
                </Button>
                <Button
                  onClick={() => createStripeCheckout('My Daily Affirmations Book - Physical', 'physical', 12.99, 'gbp')}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white text-sm"
                  data-testid="affirm-book-stripe-gbp"
                >
                  <CreditCard className="mr-2 h-3 w-3" />
                  Pay £12.99 — Card · Apple · Google
                </Button>
                <a 
                  href={`https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=abiolateejae@gmail.com&item_name=My Daily Affirmations Book - Physical&amount=12.99&currency_code=USD`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white text-sm">
                    Order via PayPal
                  </Button>
                </a>
                <div className="text-xs text-gray-600 border-t border-cyan-200 pt-2">
                  <strong>Naira Payment: ₦10,500</strong><br/>
                  Access Bank 0779703304 (Abiola Oluwatobi Ibukunoluwa)<br/>
                  Send receipt to: abiolateejae@gmail.com
                </div>
              </div>
            </Card>

            <Card id="daily-affirmations-workbook" className="p-6 hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 hover:-translate-y-2 scroll-mt-24">
              <div className="mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_leadership-impact-3/artifacts/h1ue39z8_278b84ba-a911-4fa6-93ad-82db1566d7b5.jpeg"
                  alt="My Daily Affirmations Workbook"
                  className="w-full rounded-lg shadow-lg"
                />
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-3">My Daily Affirmations Workbook</h4>
              <p className="text-gray-600 mb-4">An interactive workbook helping children internalize positive affirmations through engaging activities.</p>
              <div className="space-y-3">
                <div className="text-lg font-bold text-gray-900">$12.99 / €12.99 / £12.99 - Physical Book</div>
                <Button
                  onClick={() => createStripeCheckout('My Daily Affirmations Workbook - Physical', 'physical', 12.99, 'usd')}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white text-sm"
                  data-testid="affirm-workbook-stripe-usd"
                >
                  <CreditCard className="mr-2 h-3 w-3" />
                  Pay $12.99 — Card · Apple · Google
                </Button>
                <Button
                  onClick={() => createStripeCheckout('My Daily Affirmations Workbook - Physical', 'physical', 12.99, 'eur')}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white text-sm"
                  data-testid="affirm-workbook-stripe-eur"
                >
                  <CreditCard className="mr-2 h-3 w-3" />
                  Pay €12.99 — Card · Apple · Google
                </Button>
                <Button
                  onClick={() => createStripeCheckout('My Daily Affirmations Workbook - Physical', 'physical', 12.99, 'gbp')}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white text-sm"
                  data-testid="affirm-workbook-stripe-gbp"
                >
                  <CreditCard className="mr-2 h-3 w-3" />
                  Pay £12.99 — Card · Apple · Google
                </Button>
                <a 
                  href={`https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=abiolateejae@gmail.com&item_name=My Daily Affirmations Workbook - Physical&amount=12.99&currency_code=USD`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-sm">
                    Order via PayPal
                  </Button>
                </a>
                <div className="text-xs text-gray-600 border-t border-purple-200 pt-2">
                  <strong>Naira Payment: ₦10,500</strong><br/>
                  Access Bank 0779703304 (Abiola Oluwatobi Ibukunoluwa)<br/>
                  Send receipt to: abiolateejae@gmail.com
                </div>
              </div>
            </Card>

            <Card id="daily-affirmations-coloring" className="p-6 hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-pink-50 to-red-50 border-2 border-pink-200 hover:-translate-y-2 scroll-mt-24">
              <div className="mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_leadership-impact-3/artifacts/89l6cd42_1365d2df-c465-4e25-8f10-1b68715d169a.jpeg"
                  alt="My Daily Affirmations Coloring Booklet"
                  className="w-full rounded-lg shadow-lg"
                />
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-3">My Daily Affirmations Coloring Booklet</h4>
              <p className="text-gray-600 mb-4">A creative coloring experience that combines art with positive self-talk for young children.</p>
              <div className="space-y-3">
                <div className="text-lg font-bold text-gray-900">$12.99 / €12.99 / £12.99 - Physical Book</div>
                <Button
                  onClick={() => createStripeCheckout('My Daily Affirmations Coloring Booklet - Physical', 'physical', 12.99, 'usd')}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white text-sm"
                  data-testid="affirm-coloring-stripe-usd"
                >
                  <CreditCard className="mr-2 h-3 w-3" />
                  Pay $12.99 — Card · Apple · Google
                </Button>
                <Button
                  onClick={() => createStripeCheckout('My Daily Affirmations Coloring Booklet - Physical', 'physical', 12.99, 'eur')}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white text-sm"
                  data-testid="affirm-coloring-stripe-eur"
                >
                  <CreditCard className="mr-2 h-3 w-3" />
                  Pay €12.99 — Card · Apple · Google
                </Button>
                <Button
                  onClick={() => createStripeCheckout('My Daily Affirmations Coloring Booklet - Physical', 'physical', 12.99, 'gbp')}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white text-sm"
                  data-testid="affirm-coloring-stripe-gbp"
                >
                  <CreditCard className="mr-2 h-3 w-3" />
                  Pay £12.99 — Card · Apple · Google
                </Button>
                <a 
                  href={`https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=abiolateejae@gmail.com&item_name=My Daily Affirmations Coloring Booklet - Physical&amount=12.99&currency_code=USD`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="w-full bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 text-white text-sm">
                    Order via PayPal
                  </Button>
                </a>
                <div className="text-xs text-gray-600 border-t border-pink-200 pt-2">
                  <strong>Naira Payment: ₦10,500</strong><br/>
                  Access Bank 0779703304 (Abiola Oluwatobi Ibukunoluwa)<br/>
                  Send receipt to: abiolateejae@gmail.com
                </div>
              </div>
            </Card>

            <Card className="p-6 hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 hover:-translate-y-2">
              <div className="mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_leadership-impact-3/artifacts/c7nqs8ik_Modern_Adorable_Childrens_Room_Wall_Art_Poster_Frame_Mockup_Instagram_Post_-_1.png"
                  alt="My Daily Affirmations Poster"
                  className="w-full rounded-lg shadow-lg"
                />
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-3">My Daily Affirmations Poster</h4>
              <p className="text-gray-600 mb-4">Beautiful wall art poster featuring daily affirmations to inspire and encourage children every day.</p>
              <div className="space-y-3">
                <div className="text-lg font-bold text-gray-900">$3.00 - Physical Poster</div>
                <Button 
                  onClick={() => createStripeCheckout('My Daily Affirmations Poster - Physical', 'physical', 3, 'usd')}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white text-sm"
                >
                  <CreditCard className="mr-2 h-3 w-3" />
                  Pay with Card/Apple/Google Pay
                </Button>
                <a 
                  href={`https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=abiolateejae@gmail.com&item_name=My Daily Affirmations Poster - Physical&amount=3.00&currency_code=USD`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white text-sm">
                    Order via PayPal
                  </Button>
                </a>
                <div className="text-xs text-gray-600 border-t border-green-200 pt-2">
                  <strong>Naira Payment: ₦2,500</strong><br/>
                  Access Bank 0779703304 (Abiola Oluwatobi Ibukunoluwa)<br/>
                  Send receipt to: abiolateejae@gmail.com
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Mothering & Modelling Book */}
        <div className="border-t-2 border-gray-200 pt-16 mt-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-serif font-bold text-gray-800 mb-4">Also Coming Soon</h3>
            <p className="text-lg text-gray-600">Another powerful addition to the collection</p>
          </div>
          <div className="max-w-4xl mx-auto">
            <Card className="p-8 hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-rose-50 to-orange-50 border-2 border-rose-200">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="flex justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_leadership-impact-3/artifacts/9om5lxgi_IMG_7840.png"
                    alt="Mothering & Modelling Book"
                    className="w-full max-w-sm rounded-lg shadow-xl hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="space-y-4">
                  <h4 className="text-3xl font-serif font-bold text-gray-900">Mothering & Modelling: A Girl's First Girl</h4>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    An insightful exploration of the profound mother-daughter relationship and how mothers shape their daughters' understanding of womanhood, identity, and self-worth.
                  </p>
                  <p className="text-gray-600">
                    A companion to "Strong Roots, Strong Wings," examining the critical role mothers play in their daughters' lives.
                  </p>
                  
                  {/* Order Options */}
                  <div className="space-y-4 pt-4">
                    <h5 className="text-lg font-bold text-gray-800">Order Now:</h5>
                    
                    {/* Physical Book */}
                    <div className="border-2 border-rose-200 rounded-lg p-4 bg-white">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h6 className="font-bold text-gray-900">Physical Book</h6>
                          <p className="text-xs text-gray-600">Paperback - Ships when available</p>
                        </div>
                        <div className="text-xl font-bold text-rose-600">$9.99</div>
                      </div>
                      <div className="space-y-2">
                        <Button 
                          onClick={() => createStripeCheckout('Mothering and Modelling - Physical Book (Pre-order)', 'physical', 9.99, 'usd')}
                          className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white text-sm"
                        >
                          <CreditCard className="mr-2 h-3 w-3" />
                          Pay with Card/Apple/Google Pay
                        </Button>
                        <a 
                          href={`https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=abiolateejae@gmail.com&item_name=Mothering and Modelling - Physical Book (Pre-order)&amount=9.99&currency_code=USD`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm">
                            Pre-order via PayPal ($9.99)
                          </Button>
                        </a>
                      </div>
                    </div>

                    {/* Ebook Pre-order */}
                    <div className="border-2 border-orange-200 rounded-lg p-4 bg-white">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h6 className="font-bold text-gray-900">Digital Ebook</h6>
                          <p className="text-xs text-gray-600">PDF - Instant delivery when available</p>
                        </div>
                        <div className="text-xl font-bold text-orange-600">$6</div>
                      </div>
                      <div className="space-y-2">
                        <Button 
                          onClick={() => createStripeCheckout('Mothering and Modelling - Ebook (Pre-order)', 'ebook', 6, 'usd')}
                          className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white text-sm"
                        >
                          <CreditCard className="mr-2 h-3 w-3" />
                          Pay with Card/Apple/Google Pay
                        </Button>
                        <a 
                          href={`https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=abiolateejae@gmail.com&item_name=Mothering and Modelling - Ebook PDF (Pre-order)&amount=6.00&currency_code=USD`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button className="w-full bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white text-sm">
                            Pre-order Ebook via PayPal ($6)
                          </Button>
                        </a>
                      </div>
                    </div>

                    {/* Naira Payment */}
                    <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50/50">
                      <h6 className="text-sm font-bold text-gray-900 mb-2">Pay in Naira (Nigeria)</h6>
                      <div className="space-y-1 text-xs text-gray-700">
                        <p><strong>Physical Book:</strong> ₦7,000 | <strong>Ebook:</strong> ₦5,000</p>
                        <p><strong>Bank:</strong> Access Bank | <strong>Acct:</strong> 0779703304</p>
                        <p><strong>Name:</strong> Abiola Oluwatobi Ibukunoluwa</p>
                        <p className="text-xs text-gray-600 pt-1">
                          Send receipt to: <a href="mailto:abiolateejae@gmail.com" className="text-green-600 font-semibold">abiolateejae@gmail.com</a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Books;
